import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Tag, 
  Box, 
  X, 
  Save, 
  Layers, 
  Printer,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [variants, setVariants] = useState([]);
  const [settings, setSettings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({ name: '', price: '', cost: '', stock: '', lowStockThreshold: '', categoryId: '', icon: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes, setRes] = await Promise.all([
        api.get('/products'),
        api.get('/products/categories'),
        api.get('/settings')
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setSettings(setRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const currencySymbol = settings.currency_symbol || '$';

  const openModal = (product = null) => {
    setCurrentProduct(product);
    if (product) {
      setFormData({ 
        name: product.name, 
        price: product.price, 
        cost: product.cost || 0, 
        stock: product.stock, 
        lowStockThreshold: product.lowStockThreshold || 5,
        categoryId: product.categoryId || '',
        icon: product.icon || ''
      });
      setVariants(product.variants || []);
    } else {
      setFormData({ name: '', price: '', cost: '', stock: '', lowStockThreshold: 5, categoryId: '', icon: '' });
      setVariants([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentProduct(null);
    setVariants([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...formData, variants };
    try {
      if (currentProduct) await api.put(`/products/${currentProduct.id}`, payload);
      else await api.post('/products', payload);
      closeModal();
      loadData();
    } catch (err) {
      alert('Error saving product');
    }
  };

  const addVariant = () => setVariants([...variants, { name: '', priceAdj: 0 }]);
  const removeVariant = (idx) => setVariants(variants.filter((_, i) => i !== idx));
  const updateVariant = (idx, key, val) => {
    const n = [...variants];
    n[idx][key] = val;
    setVariants(n);
  };

  const bulkImport = async () => {
    const input = window.prompt("Paste products JSON here (array of {name, price, cost, stock}):");
    if (!input) return;
    try {
      const data = JSON.parse(input);
      await api.post('/products/bulk', { products: data });
      loadData();
      alert('Bulk import successful!');
    } catch (e) {
      alert('Invalid JSON format');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      alert('Error deleting product');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} products?`)) return;
    const idsToDelete = Array.from(selectedIds);
    try {
      await api.post('/products/bulk-delete', { ids: idsToDelete });
      setProducts(prev => prev.filter(p => !selectedIds.has(p.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert('Error deleting products');
    }
  };

  const printBarcodeLabel = (product) => {
    const win = window.open('', '_blank', 'width=400,height=300');
    win.document.write(`
      <html>
        <head>
          <title>Print Label - ${product.sku}</title>
          <style>
            @page { size: 2in 1in; margin: 0; }
            body { margin: 0; padding: 10px; font-family: monospace; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; box-sizing: border-box; }
            h3 { margin: 0; font-size: 12px; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
            svg { max-width: 100%; height: auto; margin-top: 5px; }
            p { margin: 2px 0 0; font-size: 12px; font-weight: bold; }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.0/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <h3>${product.name}</h3>
          <svg id="barcode"></svg>
          <p>${currencySymbol}${product.price.toFixed(2)}</p>
          <script>
            JsBarcode("#barcode", "${product.sku}", {
              format: "CODE128",
              width: 1.5,
              height: 30,
              displayValue: true,
              fontSize: 12,
              margin: 0
            });
            setTimeout(() => { window.print(); window.close(); }, 500);
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase()));

  // Pagination Logic
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentProducts.map(p => p.id)));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-7xl mx-auto pb-10">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Product Catalog</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Manage your inventory, pricing, and stock levels.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button onClick={bulkImport} className="bg-theme-card border border-theme hover:border-brand text-theme-main px-4 py-2 rounded-xl shadow-sm font-bold text-sm flex items-center gap-2 transition-all">
             Bulk Import
          </button>
          <Link to="/app/categories" className="bg-theme-card border border-theme hover:border-brand text-theme-main px-4 py-2 rounded-xl shadow-sm font-bold text-sm flex items-center gap-2 transition-all">
            <Layers size={18} /> Categories
          </Link>
          <button onClick={() => openModal()} className="bg-brand hover:bg-brand-light text-white px-4 py-2 rounded-xl shadow-md font-bold text-sm flex items-center gap-2 transition-all">
            <Plus size={18} /> Add Product
          </button>
        </div>
      </div>

      <div className="bg-theme-card rounded-[2rem] shadow-soft border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
          <div className="relative w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-theme-muted" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 sm:text-sm rounded-xl py-2.5 bg-theme-main border border-theme text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all"
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-theme-muted uppercase tracking-widest">
            <Tag size={16} /> {filteredProducts.length} items
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                {isAdmin && (
                  <th className="pl-6 py-4 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentProducts.length && currentProducts.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>Product Details</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4 text-right">Price</th>
                <th className="px-6 py-4 text-right">Stock</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentProducts.map(p => (
                <tr key={p.id} className={`hover:bg-theme-main/10 transition-colors group ${selectedIds.has(p.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-6 py-4">
                      <button onClick={() => toggleSelect(p.id)} className={selectedIds.has(p.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(p.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center text-brand font-black border border-brand/20 shadow-sm text-lg">
                        {p.icon ? p.icon : p.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-theme-main flex items-center gap-2">
                          {p.name}
                          {p.variants?.length > 0 && <span className="text-[10px] bg-brand/10 text-brand px-1.5 py-0.5 rounded-lg font-black uppercase tracking-tight">{p.variants.length}</span>}
                        </div>
                        <div className="text-[10px] text-theme-muted font-mono uppercase tracking-widest">{p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {p.category ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-theme-main text-theme-muted border border-theme">
                        {p.category.name}
                      </span>
                    ) : (
                      <span className="text-[10px] text-theme-muted italic opacity-50">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right font-black text-theme-main">{currencySymbol}{p.price.toFixed(2)}</td>
                  <td className="px-6 py-4 text-right text-theme-muted">
                    <div className="flex items-center justify-end gap-1 font-bold">
                      <Box size={14} className="opacity-50" />
                      {p.stock}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {p.stock > (p.lowStockThreshold || 5) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                        In Stock
                      </span>
                    ) : p.stock > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-500 border border-amber-500/20">
                        Low Stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-rose-500/10 text-rose-500 border border-rose-500/20">
                        Out of Stock
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => printBarcodeLabel(p)} className="p-1.5 text-theme-muted hover:text-indigo-500 hover:bg-indigo-500/10 rounded-lg transition-all" title="Print Barcode">
                        <Printer size={16} />
                      </button>
                      <button onClick={() => openModal(p)} className="p-1.5 text-theme-muted hover:text-brand hover:bg-theme-main rounded-lg transition-all">
                        <Edit2 size={16} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(p.id)} className="p-1.5 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentProducts.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? "7" : "6"} className="px-8 py-20 text-center text-theme-muted italic">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> products
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronLeft size={20} />
              </button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal remains the same */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-theme">
            <div className="px-6 py-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">{currentProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <button onClick={closeModal} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Product Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all" placeholder="e.g. Wireless Mouse" />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Icon / Emoji</label>
                <input type="text" maxLength="2" value={formData.icon} onChange={e => setFormData({...formData, icon: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all text-xl" placeholder="🖱️" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Price ({currencySymbol})</label>
                  <input required type="number" step="0.01" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-brand font-black focus:ring-2 focus:ring-brand outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Stock</label>
                  <input required type="number" value={formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-black focus:ring-2 focus:ring-brand outline-none transition-all" />
                </div>
              </div>

              {/* Variants Section */}
              <div className="pt-4 mt-4 border-t border-theme">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-xs font-black text-theme-main uppercase tracking-widest">Variants / Options</h4>
                  <button type="button" onClick={addVariant} className="text-[10px] text-brand font-black hover:text-brand-light flex items-center gap-1 bg-brand/10 px-2.5 py-1.5 rounded-lg transition-all uppercase">
                    <Plus size={14} /> Add Variant
                  </button>
                </div>
                <div className="space-y-3">
                  {variants.map((v, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-theme-main/20 p-3 rounded-xl border border-theme transition-all hover:border-theme-muted">
                      <input className="flex-1 text-sm bg-transparent border-none focus:ring-0 font-bold text-theme-main placeholder-theme-muted" placeholder="Variant name" value={v.name} onChange={e => updateVariant(idx, 'name', e.target.value)} />
                      <div className="flex items-center gap-1 bg-theme-card px-2 py-1.5 rounded-lg border border-theme">
                        <span className="text-[10px] font-black text-theme-muted">+{currencySymbol}</span>
                        <input type="number" step="0.01" className="w-16 text-xs border-none focus:ring-0 p-0 text-right font-black text-brand bg-transparent" value={v.priceAdj} onChange={e => updateVariant(idx, 'priceAdj', e.target.value)} />
                      </div>
                      <button type="button" onClick={() => removeVariant(idx)} className="text-theme-muted hover:text-rose-500 p-1 transition-colors"><X size={18} /></button>
                    </div>
                  ))}
                  {variants.length === 0 && <p className="text-[10px] text-theme-muted font-bold uppercase tracking-widest text-center py-4 opacity-50">No variants added.</p>}
                </div>
              </div>

              <div className="pt-6 border-t border-theme flex gap-3 mt-6">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-bold hover:bg-theme-main transition-all">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-3 bg-brand hover:bg-brand-light text-white rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2">
                  <Save size={18} /> Save Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}